import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from "axios";
import { store } from "../Redux/index";
import { setToken, clearToken } from "../Redux/authSlice";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_AUTH_URL || "http://localhost:8000/api",
});

// --- Request Interceptor ---
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const state = store.getState();
    const token = state.auth?.token;
    if (token) {
      config.headers.set("Authorization", `Bearer +${token}`);
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// --- Response Interceptor ---
// On 401: attempt a token refresh once, then retry the original request.
// If the refresh itself fails, clear auth state (forces the user back to login).
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const state = store.getState();
      const refreshToken = state.auth?.refreshToken;

      if (refreshToken) {
        try {
          const refreshRes = await axios.post(
            `${process.env.REACT_APP_AUTH_URL}authenticate/refresh`,
            { refreshToken }
          );

          const data = refreshRes.data?.result?.data;
          if (!data?.accessToken) throw new Error("No access token in refresh response");

          store.dispatch(setToken({
            token: data.accessToken,
            refreshToken: data.refreshToken ?? refreshToken,
            expiry: data.expiresIn,
            userId: state.auth?.userId,
            username: state.auth?.username,
          }));

          // Retry original request with the new token
          originalRequest.headers?.set("Authorization", `Bearer +${data.accessToken}`);
          return axiosInstance(originalRequest);
        } catch {
          store.dispatch(clearToken());
        }
      } else {
        store.dispatch(clearToken());
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
